import { useCallback, useEffect, useState } from 'react';
import { adjustVoucher, deleteVoucher, getVoucher, getVoucherMovements, redeemVoucher } from '../services/voucherService';
import { hydrateIssueMovementTimes } from '../utils/voucherMovements';

const DEFAULT_MOVEMENTS_META = {
  page: 0,
  size: 10,
  totalElements: 0,
  totalPages: 1,
  sortBy: 'performedAt',
  direction: 'desc',
};

function getVoucherActionError(error, fallbackMessage) {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.message || error?.response?.data;

  if (status === 409) {
    return typeof apiMessage === 'string' && apiMessage.trim()
      ? apiMessage
      : 'No hay horas disponibles suficientes para realizar ese canje.';
  }

  return typeof apiMessage === 'string' && apiMessage.trim()
    ? apiMessage
    : fallbackMessage;
}

export function useVoucherDetail(voucherId, actor, initialVoucher = null) {
  const [voucher, setVoucher] = useState(initialVoucher);
  const [movements, setMovements] = useState([]);
  const [movementsMeta, setMovementsMeta] = useState(DEFAULT_MOVEMENTS_META);
  const [loading, setLoading] = useState(Boolean(voucherId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (params = DEFAULT_MOVEMENTS_META) => {
    if (!voucherId) {
      setVoucher(null);
      setMovements([]);
      setError('No se ha recibido un identificador de bono válido.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [voucherResult, movementsResult] = await Promise.all([
        getVoucher(voucherId),
        getVoucherMovements(voucherId, {
          page: params.page,
          size: params.size,
          sortBy: params.sortBy,
          direction: params.direction,
        }),
      ]);

      setVoucher(voucherResult);
      setMovements(hydrateIssueMovementTimes(movementsResult.items, voucherResult.purchaseDate));
      setMovementsMeta({
        page: movementsResult.page,
        size: movementsResult.size,
        totalElements: movementsResult.totalElements,
        totalPages: movementsResult.totalPages,
        sortBy: movementsResult.sortBy || params.sortBy,
        direction: movementsResult.direction || params.direction,
      });
    } catch (_error) {
      setError('No se pudo cargar el bono.');
    } finally {
      setLoading(false);
    }
  }, [voucherId]);

  useEffect(() => {
    load(DEFAULT_MOVEMENTS_META);
  }, [load]);

  async function redeem(hours) {
    setBusy(true);
    setError('');

    if (voucher && hours > voucher.hoursAvailable) {
      const message = 'No hay horas disponibles suficientes para realizar ese canje.';
      setError(message);
      setBusy(false);
      return false;
    }

    try {
      await redeemVoucher(voucherId, {
        hours,
        performedByUserId: actor?.id || null,
        performedByName: actor?.name || actor?.fullName || actor?.email || 'Operador',
      });
      await load(movementsMeta);
      return true;
    } catch (error) {
      setError(getVoucherActionError(error, 'No se pudo realizar el canje.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function adjust(payload) {
    setBusy(true);
    setError('');

    if (payload.hours < 0 && voucher && voucher.hoursAvailable + payload.hours < 0) {
      setBusy(false);
      const message = 'No hay horas disponibles suficientes para aplicar ese ajuste.';
      setError(message);
      throw new Error(message);
    }

    try {
      await adjustVoucher(voucherId, {
        ...payload,
        performedByUserId: actor?.id || null,
        performedByName: actor?.name || actor?.fullName || actor?.email || 'Operador',
      });
      await load(movementsMeta);
    } catch (_error) {
      const message = 'No se pudo guardar el ajuste.';
      setError(message);
      throw new Error(message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError('');

    try {
      await deleteVoucher(voucherId);
      return true;
    } catch (error) {
      setError(getVoucherActionError(error, 'No se pudo eliminar el bono.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  return {
    voucher,
    movements,
    movementsMeta,
    loading,
    busy,
    error,
    redeem,
    adjust,
    remove,
    reload: load,
  };
}
